import { useForm, Link } from '@inertiajs/react'
import { Layout } from '@/components/Layout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { TimezoneSelect } from '@/components/TimezoneSelect'
import { ArrowLeft } from 'lucide-react'

interface Client {
  id: string
  name: string
}

interface N8nInstance {
  id: string
  client_id: string
  name: string
  base_url: string
  api_key: string
  timezone: string | null
}

interface Props {
  instance: N8nInstance
  clients: Client[]
  errors: Record<string, string[]>
}

export default function N8nInstanceEdit({ instance, clients, errors }: Props) {
  const { data, setData, patch, processing } = useForm({
    n8n_instance: {
      client_id: instance.client_id,
      name: instance.name,
      base_url: instance.base_url,
      api_key: instance.api_key,
      timezone: instance.timezone || '',
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/n8n_instances/${instance.id}`)
  }

  const formData = data.n8n_instance
  const setFormData = (key: keyof typeof formData, value: string) => {
    setData('n8n_instance', { ...formData, [key]: value })
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link href={`/n8n_instances/${instance.id}`}>
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back</span>
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Edit Instance</h1>
            <p className="text-muted-foreground">
              Update the settings for {instance.name}.
            </p>
          </div>
        </div>

        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Instance Details</CardTitle>
            <CardDescription>
              Modify the connection details for the n8n instance.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="client_id">Client</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => setFormData('client_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.client_id && (
                  <p className="text-sm text-destructive">{errors.client_id[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Instance Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData('name', e.target.value)}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="base_url">Base URL</Label>
                <Input
                  id="base_url"
                  value={formData.base_url}
                  onChange={(e) => setFormData('base_url', e.target.value)}
                />
                {errors.base_url && (
                  <p className="text-sm text-destructive">{errors.base_url[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="api_key">API Key</Label>
                <Input
                  id="api_key"
                  type="password"
                  value={formData.api_key}
                  onChange={(e) => setFormData('api_key', e.target.value)}
                  placeholder="Leave blank to keep existing"
                />
                <p className="text-sm text-muted-foreground">
                  Generate an API key in your n8n instance settings.
                </p>
                {errors.api_key && (
                  <p className="text-sm text-destructive">{errors.api_key[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Timezone (optional)</Label>
                <TimezoneSelect
                  value={formData.timezone}
                  onValueChange={(value) => setFormData('timezone', value)}
                  placeholder="Select timezone..."
                />
                {errors.timezone && (
                  <p className="text-sm text-destructive">{errors.timezone[0]}</p>
                )}
              </div>

              {errors.base && (
                <p className="text-sm text-destructive">{errors.base[0]}</p>
              )}

              <div className="flex gap-4">
                <Button type="submit" disabled={processing}>
                  {processing ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" asChild>
                  <Link href={`/n8n_instances/${instance.id}`}>Cancel</Link>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}
